import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DropdownComponent } from '@app/components/dropdown/dropdown.component';
import { DatePickerComponent } from '@app/components/date-picker/date-picker.component';
import { ButtonComponent } from '@app/components/button/button.component';
import { HttpClient } from '@angular/common/http';
import { RegionDictionaryNode } from '@app/services/region-dictionary.service';
import {PrimaryButton} from '@components/button/primary-button/primary-button';
import {SecondaryButton} from '@components/button/secondary-button/secondary-button';

export interface UploadRequestData {
  satellite: string;
  date: Date | null;
  region: string;
  parentRegion?: string; // ID родительского региона (level 2)
}

@Component({
  selector: 'app-upload-request-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    DropdownComponent,
    DatePickerComponent,
    ButtonComponent,
    PrimaryButton,
    SecondaryButton
  ],
  templateUrl: './upload-request-modal.component.html',
  styleUrls: ['./upload-request-modal.component.scss']
})
export class UploadRequestModalComponent implements OnInit, OnChanges {
  @Input() isOpen: boolean = false;
  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() submit = new EventEmitter<UploadRequestData>();
  @Input() regionTree: RegionDictionaryNode[] = [];
  @Input() getLocalizedRegionName: ((node: RegionDictionaryNode) => string) | null = null;
  @Input() getAllRegionsLabel: (() => string) | null = null;

  formData: UploadRequestData = {
    satellite: '',
    date: null,
    region: '',
    parentRegion: ''
  };

  satelliteOptions = [
    { label: 'Sentinel', value: 'sentinel' },
    { label: 'Landsat', value: 'landsat' }
  ];

  // Состояние для дерева регионов
  showRegionDropdown: boolean = false;
  regionSearch: string = '';
  filteredRegionTree: RegionDictionaryNode[] = [];
  expandedRegionIds: Set<string> = new Set();
  selectedRegionLabel: string = '';
  selectedRegion: string = '';
  selectedParentRegion: string = '';

  constructor(
    private http: HttpClient,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    if (this.regionTree && this.regionTree.length > 0) {
      this.filteredRegionTree = this.buildFilteredRegionTree('');
      this.selectedRegionLabel = this.getAllRegionsLabel ? this.getAllRegionsLabel() : this.translate.instant('ALL_REGIONS');
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['regionTree'] && this.regionTree && this.regionTree.length > 0) {
      this.filteredRegionTree = this.buildFilteredRegionTree(this.regionSearch);
      if (!this.selectedRegion && !this.selectedParentRegion) {
        this.selectedRegionLabel = this.getAllRegionsLabel ? this.getAllRegionsLabel() : this.translate.instant('ALL_REGIONS');
      }
    }
  }

  private buildFilteredRegionTree(searchTerm: string): RegionDictionaryNode[] {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return this.regionTree.map(region => ({
        ...region,
        children: region.children ? region.children.map((child: RegionDictionaryNode) => ({ ...child })) : []
      }));
    }

    const filtered: RegionDictionaryNode[] = [];

    this.regionTree.forEach(region => {
      if (region.id === 'all') {
        const regionName = this.getLocalizedRegionName ? this.getLocalizedRegionName(region) : region.name || '';
        if (regionName.toLowerCase().includes(term)) {
          filtered.push({ ...region, children: [] });
        }
        return;
      }

      const regionName = this.getLocalizedRegionName ? this.getLocalizedRegionName(region) : region.name || '';
      const matchesRegion = regionName.toLowerCase().includes(term);
      const matchingChildren =
        region.children
          ?.filter((child: RegionDictionaryNode) => {
            const childName = this.getLocalizedRegionName ? this.getLocalizedRegionName(child) : child.name || '';
            return childName.toLowerCase().includes(term);
          })
          .map((child: RegionDictionaryNode) => ({ ...child })) ?? [];

      if (matchesRegion || matchingChildren.length) {
        filtered.push({
          ...region,
          expanded: matchesRegion || matchingChildren.length > 0,
          children: matchesRegion ? (region.children ? region.children.map((child: RegionDictionaryNode) => ({ ...child })) : []) : matchingChildren
        });
      }
    });

    return filtered;
  }

  toggleRegionDropdown(): void {
    this.showRegionDropdown = !this.showRegionDropdown;
  }

  onRegionSearchChange(): void {
    this.filteredRegionTree = this.buildFilteredRegionTree(this.regionSearch);
  }

  toggleRegionExpansion(region: RegionDictionaryNode, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.expandedRegionIds.has(region.id)) {
      this.expandedRegionIds.delete(region.id);
    } else {
      this.expandedRegionIds.add(region.id);
    }
  }

  isRegionExpanded(regionId: string): boolean {
    return this.expandedRegionIds.has(regionId);
  }

  selectRegion(region: RegionDictionaryNode, closeDropdown: boolean = true): void {
    if (region.id === 'all') {
      this.selectedRegionLabel = this.getAllRegionsLabel ? this.getAllRegionsLabel() : this.translate.instant('ALL_REGIONS');
      this.selectedParentRegion = '';
      this.selectedRegion = '';
      this.formData.region = '';
      this.formData.parentRegion = '';
      if (closeDropdown) {
        this.showRegionDropdown = false;
      }
      return;
    }

    const regionName = this.getLocalizedRegionName ? this.getLocalizedRegionName(region) : region.name || '';
    this.selectedRegionLabel = regionName;
    // Выбран родительский регион (level 2) - используем parentRegionId
    this.selectedParentRegion = region.id;
    this.selectedRegion = ''; // Сбрасываем район
    this.formData.parentRegion = region.id;
    this.formData.region = '';
    if (closeDropdown) {
      this.showRegionDropdown = false;
    }
  }

  selectDistrict(parent: RegionDictionaryNode, district: RegionDictionaryNode, event?: Event): void {
    event?.stopPropagation();

    const parentName = this.getLocalizedRegionName ? this.getLocalizedRegionName(parent) : parent.name || '';
    const districtName = this.getLocalizedRegionName ? this.getLocalizedRegionName(district) : district.name || '';
    this.selectedRegionLabel = `${parentName} / ${districtName}`;
    // Выбран район (level 3) - используем regionId
    this.selectedRegion = district.id;
    this.selectedParentRegion = parent.id; // Сохраняем также родительский регион
    this.formData.region = district.id;
    this.formData.parentRegion = parent.id;
    this.showRegionDropdown = false;
  }

  clearRegionFilter(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.selectedRegion = '';
    this.selectedParentRegion = '';
    this.selectedRegionLabel = this.getAllRegionsLabel ? this.getAllRegionsLabel() : this.translate.instant('ALL_REGIONS');
    this.formData.region = '';
    this.formData.parentRegion = '';
  }

  closeModal(): void {
    this.isOpen = false;
    this.isOpenChange.emit(false);
    this.resetForm();
  }

  onSubmit(): void {
    if (this.isFormValid()) {
      this.submit.emit(this.formData);
      this.http.post('/api/mock/upload-request', this.formData).subscribe({
        next: () => {},
        error: () => {}
      });
      this.closeModal();
    }
  }

  onCancel(): void {
    this.closeModal();
  }

  private isFormValid(): boolean {
    return !!this.formData.satellite && !!this.formData.date && (!!this.formData.region || !!this.formData.parentRegion);
  }

  private resetForm(): void {
    this.formData = {
      satellite: '',
      date: null,
      region: '',
      parentRegion: ''
    };
    this.selectedRegion = '';
    this.selectedParentRegion = '';
    this.selectedRegionLabel = this.getAllRegionsLabel ? this.getAllRegionsLabel() : this.translate.instant('ALL_REGIONS');
    this.regionSearch = '';
    this.showRegionDropdown = false;
    this.expandedRegionIds.clear();
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal-backdrop')) {
      this.closeModal();
    }
  }
}
